import { Grid } from '@mui/joy'
import { useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { paymentProcedureEnumLabels } from '~/shared/config/constants'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { Modal } from '~/shared/ui/Modal'
import { FieldData } from './FieldData'
import { getDayOffParams } from './helpers'
import { List, mapPaymentSchedulesToRows } from './List'
import { $paymentsTemplate } from './model'

type PaymentScheduleModalProps = {
  isShow: boolean
  onCancel: () => void
}

export function PaymentScheduleModal({
  isShow,
  onCancel,
}: PaymentScheduleModalProps) {
  const { t } = useTranslation()

  const { rentalContract, paymentsSchedule } = useStoreMap(
    $paymentsTemplate,
    (template) => {
      const rentalContract = template?.rentalContract
      return {
        rentalContract: {
          ...rentalContract,
          createdAt: rentalContract?.createdAt
            ? formatDateTimeForUI(rentalContract?.createdAt)
            : '',
        },
        paymentsSchedule: mapPaymentSchedulesToRows(
          template?.paymentsTemplate.data || [],
          rentalContract?.workSchedule &&
            t(paymentProcedureEnumLabels[rentalContract.workSchedule]),
        ),
      }
    },
  )

  const { contractNumber, rentPerDay, createdAt, workSchedule, dayOff } =
    rentalContract

  const scheduleText = useMemo(() => {
    if (dayOff) {
      const { textPrefix, dayOffLabel } = getDayOffParams(dayOff)

      return `${workSchedule} ${t('day of.day off -', {
        textPrefix: t(textPrefix),
        dayOff: t(dayOffLabel),
      })}`
    }
    return workSchedule
  }, [dayOff, t, workSchedule])

  return (
    <Modal
      isShow={isShow}
      onCancel={onCancel}
      title={t('schedule of debits under contract number', { contractNumber })}
      dialogProps={{
        sx: {
          minWidth: '1000px',
          maxWidth: '1000px',
        },
      }}
    >
      <Grid container spacing={2} sx={{ marginBottom: '32px' }}>
        <FieldData
          label={t('daily rental cost')}
          value={Number(rentPerDay).toLocaleString()}
        />
        <FieldData label={t('contract start date')} value={createdAt} />
        <FieldData label={t('work schedule')} value={scheduleText} />
      </Grid>
      <List rows={paymentsSchedule} />
    </Modal>
  )
}
